<script setup>
console.log('🚀 GM Sentiment Trend Chart: Initializing...');

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, registerables } from 'chart.js';
import GmChartControls from '../filters/GmChartControls.vue';
import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

Chart.register(...registerables);

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Sentiment Trend'
  },
  height: {
    type: Number,
    default: 300
  },
  channels: {
    type: Array,
    default: () => []
  },
  agents: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['refresh', 'export', 'filters-change']);

const { t } = useI18n();

console.log('📊 GM Sentiment Trend Chart: Props received:', {
  data: props.data,
  title: props.title
});

// Reactive data
const isLoading = ref(false);
const lastUpdated = ref(null);
const filters = ref({
  dateRange: { from: null, to: null, preset: 'last_30_days' },
  period: { type: 'month', value: null },
  channel: null,
  agent: null
});
const filteredData = ref([]);
const chartInstance = ref(null);
const chartCanvas = ref(null);

// Chart configuration
const chartConfig = ref({
  type: 'line',
  data: {
    labels: [],
    datasets: [{
      label: 'Sentiment Score',
      data: [],
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#10b981',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10b981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const score = context.parsed.y;
            const percentage = (score * 100).toFixed(1);
            const sentiment = getSentimentLabel(score);
            return `${percentage}% (${sentiment})`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          }
        }
      },
      y: {
        min: 0,
        max: 1,
        grid: {
          color: '#f3f4f6',
          drawBorder: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          },
          callback: function(value) {
            return (value * 100).toFixed(0) + '%';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  }
});

// Helper function to get sentiment label
const getSentimentLabel = (score) => {
  if (score >= 0.7) return 'Positive';
  if (score >= 0.4) return 'Neutral';
  return 'Negative';
};

// Helper function to get sentiment color
const getSentimentColor = (score) => {
  if (score >= 0.7) return '#10b981';
  if (score >= 0.4) return '#f59e0b';
  return '#ef4444';
};

// Process data for chart
const processChartData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    return {
      labels: [],
      data: []
    };
  }
  
  // Process real data only
  const labels = rawData.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = rawData.map(item => item.score || 0.5);
  
  return { labels, data };
};

// Computed properties
const chartData = computed(() => {
  // Use filtered data if available, otherwise fall back to props.data
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  const processed = processChartData(dataToUse);
  return {
    ...chartConfig.value,
    data: {
      ...chartConfig.value.data,
      labels: processed.labels,
      datasets: [{
        ...chartConfig.value.data.datasets[0],
        data: processed.data
      }]
    }
  };
});

// Chart statistics
const chartStats = computed(() => {
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  if (!dataToUse || dataToUse.length === 0) return null;
  
  const values = dataToUse.map(item => item.score || 0.5);
  const average = values.reduce((sum, val) => sum + val, 0) / values.length;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const current = values[values.length - 1] || 0.5;
  const previous = values[values.length - 2] || 0.5;
  const trend = current - previous;
  
  return {
    average: Math.round(average * 100) / 100,
    max: Math.round(max * 100) / 100,
    min: Math.round(min * 100) / 100,
    current: Math.round(current * 100) / 100,
    trend: Math.round(trend * 100) / 100,
    currentLabel: getSentimentLabel(current),
    currentColor: getSentimentColor(current)
  };
});

// Methods
const updateChart = async () => {
  console.log('🔄 GM Sentiment Trend Chart: Updating chart with new data...');
  
  if (!chartCanvas.value) return;
  
  // Destroy existing chart if it exists
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
  
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  
  if (!dataToUse || dataToUse.length === 0) {
    console.log('📊 GM Sentiment Trend Chart: No data to display');
    return;
  }
  
  const labels = dataToUse.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = dataToUse.map(item => item.score || 0.5);
  
  const ctx = chartCanvas.value.getContext('2d');
  
  chartInstance.value = new Chart(ctx, {
    type: 'line',
    data: {
      labels: labels,
      datasets: [{
        label: 'Sentiment Score',
        data: data,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#3b82f6',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const value = context.parsed.y;
              const percentage = (value * 100).toFixed(1);
              const sentiment = getSentimentLabel(value);
              return `${sentiment}: ${percentage}%`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            }
          }
        },
        y: {
          min: 0,
          max: 1,
          grid: {
            color: '#f3f4f6',
            drawBorder: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            },
            callback: function(value) {
              return (value * 100).toFixed(0) + '%';
            }
          }
        }
      }
    }
  });
  
  console.log('✅ GM Sentiment Trend Chart: Chart updated successfully');
};

const fetchChartData = async () => {
  console.log('🔄 GM Sentiment Trend Chart: Fetching chart data with filters:', filters.value);
  try {
    isLoading.value = true;
    
    // Make API call to get filtered sentiment trend data
    const response = await GmAnalyticsAPI.getSentimentTrend(filters.value);
    console.log('🔍 GM Sentiment Trend Chart: API response:', response);
    
    if (response.data && response.data.success) {
      // Update the chart data with filtered results
      filteredData.value = response.data.data.sentimentTrend || [];
      console.log('✅ GM Sentiment Trend Chart: Filtered data received:', filteredData.value);
    }
    
    lastUpdated.value = new Date().toISOString();
    console.log('✅ GM Sentiment Trend Chart: Chart data fetched successfully');
    
    // Update the chart with new data
    await updateChart();
  } catch (error) {
    console.error('❌ GM Sentiment Trend Chart: Error fetching chart data:', error);
    // Fallback to original data if API fails
    filteredData.value = props.data || [];
    await updateChart();
  } finally {
    isLoading.value = false;
  }
};

const refreshChart = () => {
  console.log('🔄 GM Sentiment Trend Chart: Refreshing chart...');
  isLoading.value = true;
  
  // Emit refresh event to parent
  emit('refresh', { chartType: 'sentiment-trend', filters: filters.value });
  
  // Simulate loading
  setTimeout(() => {
    isLoading.value = false;
    lastUpdated.value = new Date().toISOString();
    updateChart();
  }, 1000);
};

const exportChart = () => {
  console.log('📊 GM Sentiment Trend Chart: Exporting chart...');
  emit('export', { chartType: 'sentiment-trend', filters: filters.value });
};

const onFiltersChange = (newFilters) => {
  console.log('🔧 GM Sentiment Trend Chart: Filters changed:', newFilters);
  filters.value = newFilters;
  emit('filters-change', { chartType: 'sentiment-trend', filters: newFilters });
  fetchChartData();
};

// Watch for external filter changes
watch(() => props.filters, (newFilters) => {
  console.log('🔧 GM Sentiment Trend Chart: Props.filters changed:', newFilters);
  console.log('🔧 GM Sentiment Trend Chart: Props.filters type:', typeof newFilters);
  console.log('🔧 GM Sentiment Trend Chart: Props.filters keys:', Object.keys(newFilters || {}));
  console.log('🔧 GM Sentiment Trend Chart: Props.filters dateRange:', newFilters?.dateRange);
  console.log('🔧 GM Sentiment Trend Chart: Props.filters preset:', newFilters?.dateRange?.preset);
  
  if (newFilters) {
    console.log('🔧 GM Sentiment Trend Chart: External filters changed:', newFilters);
    filters.value = { ...filters.value, ...newFilters };
    // Fetch new data for this chart only
    fetchChartData();
  }
}, { deep: true });

// Lifecycle
onMounted(async () => {
  console.log('🎯 GM Sentiment Trend Chart: Component mounted');
  lastUpdated.value = new Date().toISOString();
  await nextTick();
  updateChart();
});

console.log('✅ GM Sentiment Trend Chart: Component setup completed');
</script>

<template>
  <div class="gm-sentiment-trend-chart">
    <!-- Chart Controls -->
    <GmChartControls
      :title="title"
      icon="😊"
      :is-loading="isLoading"
      :last-updated="lastUpdated"
      :show-additional-filters="true"
      :channels="channels"
      :agents="agents"
      v-model="filters"
      @refresh="refreshChart"
      @export="exportChart"
      @filters-change="onFiltersChange"
    />
    
    <!-- Chart Stats -->
    <div v-if="chartStats" class="gm-chart-stats">
      <div class="gm-stat-item">
        <span class="gm-stat-label">Current</span>
        <span class="gm-stat-value" :style="{ color: chartStats.currentColor }">
          {{ (chartStats.current * 100).toFixed(1) }}%
        </span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Avg</span>
        <span class="gm-stat-value">{{ (chartStats.average * 100).toFixed(1) }}%</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Peak</span>
        <span class="gm-stat-value">{{ (chartStats.max * 100).toFixed(1) }}%</span>
      </div>
    </div>
    
    <div class="gm-chart-container">
      <div v-if="chartData.data.labels.length === 0" class="gm-chart-placeholder" :style="{ height: height + 'px' }">
        <div class="gm-chart-message">
          <div class="gm-chart-icon-large">📈</div>
          <h3>No Sentiment Data Available</h3>
          <p>Sentiment data will appear here once conversations are analyzed</p>
        </div>
      </div>
      <div v-else class="gm-chart-canvas-container" :style="{ height: height + 'px' }">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
    
    <div v-if="chartStats" class="gm-chart-footer">
      <div class="gm-trend-indicator">
        <span class="gm-trend-icon" :class="{ 'positive': chartStats.trend > 0, 'negative': chartStats.trend < 0 }">
          {{ chartStats.trend > 0 ? '↗️' : chartStats.trend < 0 ? '↘️' : '➡️' }}
        </span>
        <span class="gm-trend-text">
          {{ chartStats.trend > 0 ? '+' : '' }}{{ (chartStats.trend * 100).toFixed(1) }}% sentiment
          <span class="gm-trend-period">vs previous period</span>
        </span>
      </div>
      <div class="gm-current-sentiment">
        <span class="gm-sentiment-label">Current:</span>
        <span 
          class="gm-sentiment-value" 
          :style="{ color: chartStats.currentColor }"
        >
          {{ chartStats.currentLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-sentiment-trend-chart {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  max-height: 500px;
  overflow: hidden;
}

.gm-sentiment-trend-chart:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.gm-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.gm-chart-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-icon {
  font-size: 1.125rem;
}

.gm-chart-stats {
  display: flex;
  gap: 1rem;
}

.gm-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.gm-stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-container {
  margin-bottom: 1rem;
  height: 300px;
  overflow: hidden;
}

.gm-chart-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.gm-chart-placeholder {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  position: relative;
}

.gm-chart-message {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.gm-chart-icon-large {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.gm-chart-message h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.gm-chart-message p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.gm-chart-sample-data {
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem;
}

.gm-sample-point {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-width: 70px;
}

.gm-sample-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-sample-value {
  font-size: 0.875rem;
  font-weight: 600;
}

.gm-sample-sentiment {
  font-size: 0.6875rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: capitalize;
}

.gm-chart-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f3f4f6;
  padding-top: 1rem;
}

.gm-trend-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-trend-icon {
  font-size: 1rem;
}

.gm-trend-icon.positive {
  color: #10b981;
}

.gm-trend-icon.negative {
  color: #ef4444;
}

.gm-trend-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.gm-trend-period {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
  margin-left: 0.25rem;
}

.gm-current-sentiment {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-sentiment-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-sentiment-value {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

@media (max-width: 768px) {
  .gm-sentiment-trend-chart {
    padding: 1rem;
  }
  
  .gm-chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .gm-chart-stats {
    width: 100%;
    justify-content: space-around;
  }
  
  .gm-chart-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .gm-chart-sample-data {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .gm-sample-point {
    flex-direction: row;
    justify-content: space-between;
    min-width: auto;
  }
}
</style>
